import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { API } from '@/App';

const AdminPricingSettings = () => {
  const [pricing, setPricing] = useState({
    monthly_price: 9900,
    yearly_price: 99900,
    yearly_maintenance_fee: 9900
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPricing({
        monthly_price: response.data.monthly_price,
        yearly_price: response.data.yearly_price,
        yearly_maintenance_fee: response.data.yearly_maintenance_fee
      });
    } catch (error) {
      toast.error('Failed to load pricing settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/pricing`, pricing, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pricing updated successfully!');
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const formatCents = (cents) => {
    return (cents / 100).toFixed(2);
  };

  const parseDollars = (dollars) => {
    return Math.round(parseFloat(dollars) * 100);
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6 text-white">
        Pricing Settings
      </h1>

      <div className="grid gap-6 max-w-2xl">
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-white">
              Membership Pricing
            </h2>
            <p className="text-sm text-gray-300">
              Set your monthly and yearly membership prices
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Monthly Price */}
            <div>
              <Label htmlFor="monthly">Monthly Membership Price</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-2xl font-bold">$</span>
                <Input
                  id="monthly"
                  type="number"
                  step="0.01"
                  value={formatCents(pricing.monthly_price)}
                  onChange={(e) => setPricing({
                    ...pricing,
                    monthly_price: parseDollars(e.target.value)
                  })}
                  className="text-lg"
                />
                <span className="text-sm text-gray-500">/ month</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Current: ${formatCents(pricing.monthly_price)} per month
              </p>
            </div>

            {/* Yearly Price */}
            <div>
              <Label htmlFor="yearly">Yearly Membership Price</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-2xl font-bold">$</span>
                <Input
                  id="yearly"
                  type="number"
                  step="0.01"
                  value={formatCents(pricing.yearly_price)}
                  onChange={(e) => setPricing({
                    ...pricing,
                    yearly_price: parseDollars(e.target.value)
                  })}
                  className="text-lg"
                />
                <span className="text-sm text-gray-500">/ year</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Current: ${formatCents(pricing.yearly_price)} per year
              </p>
            </div>

            {/* Yearly Maintenance Fee */}
            <div>
              <Label htmlFor="maintenance">Yearly Maintenance Fee</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-2xl font-bold">$</span>
                <Input
                  id="maintenance"
                  type="number"
                  step="0.01"
                  value={formatCents(pricing.yearly_maintenance_fee)}
                  onChange={(e) => setPricing({
                    ...pricing,
                    yearly_maintenance_fee: parseDollars(e.target.value)
                  })}
                  className="text-lg"
                />
                <span className="text-sm text-gray-500">/ year</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Annual fee for existing members: ${formatCents(pricing.yearly_maintenance_fee)}
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                {saving ? 'Saving...' : 'Save Pricing Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card className="bg-gradient-to-r from-cyan-50 to-blue-50">
          <CardHeader>
            <h3 className="text-lg font-semibold" style={{ color: '#331a67' }}>
              Preview: What Members See
            </h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-sm text-gray-600 mb-1">Monthly Plan</div>
                <div className="text-3xl font-bold" style={{ color: '#331a67' }}>
                  ${formatCents(pricing.monthly_price)}
                </div>
                <div className="text-xs text-gray-500">per month</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-sm text-gray-600 mb-1">Yearly Plan</div>
                <div className="text-3xl font-bold" style={{ color: '#331a67' }}>
                  ${formatCents(pricing.yearly_price)}
                </div>
                <div className="text-xs text-gray-500">per year</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPricingSettings;
